// client/src/components/BidEstimation/VendorQuotes/VendorQuotesTab.js
import React, { useState, useEffect } from 'react'
import {
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, Chip, Typography, Box, IconButton, CircularProgress,
  FormControl, InputLabel, Select, MenuItem
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import CloudUploadIcon from '@material-ui/icons/CloudUpload'
import DeleteIcon from '@material-ui/icons/Delete'
import VisibilityIcon from '@material-ui/icons/Visibility'
import axios from 'axios'
import QuoteUploadModal from './QuoteUploadModal'
import QuoteReviewTable from './QuoteReviewTable'
import ManualEntryModal from './ManualEntryModal'
import BidEstimationHeader from '../shared/BidEstimationHeader'

const useStyles = makeStyles(theme => ({
  root: { padding: theme.spacing(2) },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2)
  },
  chip: { marginLeft: theme.spacing(1) }
}))

const statusColors = {
  parsed: 'default',
  reviewed: 'primary',
  imported: 'primary',
  failed: 'secondary'
}

const confidenceColors = {
  high: '#4caf50',
  medium: '#ff9800',
  low: '#f44336'
}

export default function VendorQuotesTab ({ projectId }) {
  const classes = useStyles()
  const [quotes, setQuotes] = useState([])
  const [loading, setLoading] = useState(true)
  const [uploadOpen, setUploadOpen] = useState(false)
  const [manualEntryOpen, setManualEntryOpen] = useState(false)
  const [reviewQuoteId, setReviewQuoteId] = useState(null)
  const [modules, setModules] = useState([])
  const [moduleFilter, setModuleFilter] = useState('all')

  const fetchQuotes = async () => {
    setLoading(true)
    try {
      const params = moduleFilter !== 'all' ? { moduleId: moduleFilter } : {}
      const { data } = await axios.get(`/api/bid-estimation/vendor-quotes/project/${projectId}`, { params })
      setQuotes(data)
    } catch (e) {
      console.error('Failed to fetch quotes:', e)
    }
    setLoading(false)
  }

  useEffect(() => {
    if (projectId) {
      fetchQuotes()
      axios.get(`/api/bid-estimation/projects/${projectId}/modules`).then(res => {
        setModules(res.data || [])
      }).catch(() => {})
    }
  }, [projectId])

  useEffect(() => {
    if (projectId) fetchQuotes()
  }, [moduleFilter])

  const handleUploadComplete = (newQuote) => {
    setUploadOpen(false)
    fetchQuotes()
    if (newQuote?._id) {
      setReviewQuoteId(newQuote._id)
    }
  }

  const handleDelete = async (quoteId) => {
    if (!window.confirm('Delete this vendor quote?')) return
    try {
      await axios.delete(`/api/bid-estimation/vendor-quotes/${quoteId}`)
      fetchQuotes()
    } catch (e) {
      console.error('Delete failed:', e)
    }
  }

  if (reviewQuoteId) {
    return (
      <QuoteReviewTable
        quoteId={reviewQuoteId}
        onBack={() => { setReviewQuoteId(null); fetchQuotes() }}
      />
    )
  }

  return (
    <div className={classes.root}>
      <BidEstimationHeader
        backTo={projectId ? `/bid-estimation/${projectId}` : '/bid-estimation'}
        backLabel="Project"
        breadcrumbs={[
          { label: 'Bid Estimation', to: '/bid-estimation' },
          ...(projectId ? [{ label: 'Project', to: `/bid-estimation/${projectId}` }] : []),
          { label: 'Vendor Quotes' }
        ]}
        title="Vendor Quotes"
        actions={(
          <>
            <Button
              variant='contained'
              color='primary'
              size='small'
              startIcon={<CloudUploadIcon />}
              onClick={() => setUploadOpen(true)}
            >
              Upload Quote
            </Button>
            <Button
              variant='outlined'
              size='small'
              onClick={() => setManualEntryOpen(true)}
              style={{ color: '#fff', borderColor: 'rgba(255,255,255,0.5)' }}
            >
              Manual Entry
            </Button>
          </>
        )}
      />

      {modules.length > 0 && (
        <Box style={{ marginBottom: 16 }}>
          <FormControl size="small" variant="outlined" style={{ minWidth: 200 }}>
            <InputLabel>Module</InputLabel>
            <Select value={moduleFilter} onChange={e => setModuleFilter(e.target.value)} label="Module">
              <MenuItem value="all">All Modules</MenuItem>
              {modules.map(m => (
                <MenuItem key={m._id} value={m._id}>{m.scope}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {loading ? (
        <CircularProgress />
      ) : quotes.length === 0 ? (
        <Typography color='textSecondary'>No vendor quotes uploaded yet.</Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>Vendor</TableCell>
                <TableCell>Module</TableCell>
                <TableCell>Quote #</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align='right'>Total</TableCell>
                <TableCell align='center'>Items</TableCell>
                <TableCell align='center'>Status</TableCell>
                <TableCell align='center'>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {quotes.map(q => (
                <TableRow key={q._id} hover>
                  <TableCell>{q.vendor || 'Unknown'}</TableCell>
                  <TableCell>{q.moduleId ? (modules.find(m => String(m._id) === String(q.moduleId))?.scope || '—') : '—'}</TableCell>
                  <TableCell>{q.quoteNumber || '—'}</TableCell>
                  <TableCell>{q.quoteDate ? new Date(q.quoteDate).toLocaleDateString() : '—'}</TableCell>
                  <TableCell align='right'>${(q.total || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</TableCell>
                  <TableCell align='center'>{q.lineItemCount || 0}</TableCell>
                  <TableCell align='center'>
                    <Chip
                      label={q.status}
                      size='small'
                      color={statusColors[q.status] || 'default'}
                    />
                  </TableCell>
                  <TableCell align='center'>
                    <IconButton size='small' onClick={() => setReviewQuoteId(q._id)}>
                      <VisibilityIcon fontSize='small' />
                    </IconButton>
                    <IconButton size='small' onClick={() => handleDelete(q._id)}>
                      <DeleteIcon fontSize='small' />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <QuoteUploadModal
        open={uploadOpen}
        projectId={projectId}
        modules={modules}
        onClose={() => setUploadOpen(false)}
        onComplete={handleUploadComplete}
      />

      <ManualEntryModal
        open={manualEntryOpen}
        projectId={projectId}
        modules={modules}
        onClose={() => setManualEntryOpen(false)}
        onComplete={handleUploadComplete}
      />
    </div>
  )
}
